export type SegmentedControlOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

export type SegmentedControlProps<T extends string> = {
  label: string;
  options: readonly SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  className?: string;
};

const OPTION_CLASS =
  'rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed';

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
  className,
}: SegmentedControlProps<T>) {
  const selectedIndex = options.findIndex((option) => option.value === value);

  const select = (next: T) => {
    if (disabled || next === value) return;
    if (options.find((option) => option.value === next)?.disabled) return;
    onChange(next);
  };

  // Arrow keys walk past disabled options rather than landing on them, and give
  // up once they come back around to where they started.
  const step = (offset: number) => {
    if (disabled || options.length === 0) return;
    const from = selectedIndex === -1 ? 0 : selectedIndex;
    for (let hop = 1; hop <= options.length; hop++) {
      const index = (((from + offset * hop) % options.length) + options.length) % options.length;
      if (!options[index].disabled) {
        select(options[index].value);
        return;
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      step(1);
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      step(-1);
    }
  };

  return (
    <div
      role='radiogroup'
      aria-label={label}
      className={`inline-flex gap-0.5 rounded-full border bg-muted/50 p-0.5 ${
        disabled ? 'opacity-50' : ''
      } ${className ?? ''}`}
    >
      {options.map((option) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            type='button'
            role='radio'
            aria-checked={isSelected}
            disabled={disabled || option.disabled}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => select(option.value)}
            onKeyDown={handleKeyDown}
            className={`${OPTION_CLASS} ${
              isSelected
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
